import { Pipe, PipeTransform } from '@angular/core';
import { UserService } from '../core/user/user.service';

@Pipe({
    name: 'dateFormat',
    standalone: false
})
export class DateFormatPipe implements PipeTransform {

    constructor(private user: UserService) { }

    get businessTimezone(): string {
        return this.user.activeBusiness?.timezone;
    }

    transform(value: string, format?: boolean | string, shortMonthFormat?: boolean): string {
        if (!value) return '';

        const inputDate = new Date(value);

        if (isNaN(inputDate.getTime())) return 'N/A';

        const timezone = this.businessTimezone || 'UTC';

        // Support 'longDate' format: "September 23, 2026"
        if (format === 'longDate') {
            const formatter = new Intl.DateTimeFormat('en-US', {
                timeZone: timezone,
                month: 'long',
                day: 'numeric',
                year: 'numeric',
            });
            return formatter.format(inputDate);
        }

        // Support 'timeOnly' format: "8:29am"
        if (format === 'timeOnly') {
            const formatter = new Intl.DateTimeFormat('en-US', {
                timeZone: timezone,
                hour: 'numeric',
                minute: '2-digit',
                hour12: true,
            });
            return formatter.format(inputDate).replace(' AM', 'am').replace(' PM', 'pm');
        }

        // Support 'shortTime' format: "Sep 23, 8:29am"
        if (format === 'shortTime') {
            const formatter = new Intl.DateTimeFormat('en-US', {
                timeZone: timezone,
                month: 'short',
                day: 'numeric',
                hour: 'numeric',
                minute: '2-digit',
                hour12: true,
            });
            return formatter.format(inputDate).replace(' AM', 'am').replace(' PM', 'pm');
        }

        if (shortMonthFormat) {
            const formatter = new Intl.DateTimeFormat('en-US', {
                timeZone: timezone,
                month: 'short',
                day: 'numeric',
                year: 'numeric',
            });
            return formatter.format(inputDate);
        }

        // fullDate format (when format is true)
        if (format === true) {
            const formatter = new Intl.DateTimeFormat('en-US', {
                timeZone: timezone,
                weekday: 'short',
                month: 'short',
                day: 'numeric',
                hour: 'numeric',
                minute: 'numeric',
                hour12: true,
            });
            const daysAgo = this.getDaysAgo(inputDate, new Date());
            return `${formatter.format(inputDate)} (${daysAgo})`;
        } else {
            const currentDate = new Date();
            if (this.isSameDay(inputDate, currentDate)) {
                const timeFormatter = new Intl.DateTimeFormat('en-US', {
                    timeZone: timezone,
                    hour: 'numeric',
                    minute: 'numeric',
                    hour12: true,
                });
                return timeFormatter.format(inputDate);
            } else {
                const dateFormatter = new Intl.DateTimeFormat('en-US', {
                    timeZone: timezone,
                    month: 'numeric',
                    day: 'numeric',
                    year: '2-digit',
                });
                return dateFormatter.format(inputDate);
            }
        }
    }


    private getDaysAgo(inputDate: Date, currentDate: Date): string {
        const timezone = this.businessTimezone || 'UTC';

        // Get dates in the business timezone
        const inputDateInTZ = new Date(new Intl.DateTimeFormat('en-US', {
            timeZone: timezone,
            year: 'numeric',
            month: 'numeric',
            day: 'numeric'
        }).format(inputDate));

        const currentDateInTZ = new Date(new Intl.DateTimeFormat('en-US', {
            timeZone: timezone,
            year: 'numeric',
            month: 'numeric',
            day: 'numeric'
        }).format(currentDate));

        const diffInTime = currentDateInTZ.getTime() - inputDateInTZ.getTime();
        const diffInDays = Math.floor(diffInTime / (1000 * 3600 * 24));

        if (diffInDays === 0) return 'Today';
        if (diffInDays === 1) return '1 day ago';
        return `${diffInDays} days ago`;
    }

    private isSameDay(date1: Date, date2: Date): boolean {
        const timezone = this.businessTimezone || 'UTC';

        // Format both dates in the business timezone and compare the date parts
        const formatter = new Intl.DateTimeFormat('en-US', {
            timeZone: timezone,
            year: 'numeric',
            month: 'numeric',
            day: 'numeric'
        });

        return formatter.format(date1) === formatter.format(date2);
    }
}
